import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import StatusIndicator from '@components/StatusIndicator'
import SuccessAnimation from '@components/LoadingTick'

import './loading-status-indicator.scss'

const baseClass = 'loading-status-indicator'
const successDuration = 2.5 // second
/**
 * Indicate the loading status of a scenario re-calculating. This component
 * constructs a loading indicator of the familar "stairs loader" into a status
 * indicator card for a re-calculating scenario which then updates to a success animation upon completion.
 * Additional loading indicators, in the form of an object, may be passed to show extra context for the loading process.
 * These are displayed as floating labels above the status indicator and fill disappear after their status changes.
 */
const LoadingStatusIndicator = ({
  isFetching,
  loadingIndicators,
  calculatingMessage,
  loadingMessage,
  successMessage }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [stack, setStack] = useState([]);

  const isBusy = /* true // */ isFetching || Object.values(loadingIndicators).includes(true)

  const removeFromStack = (key) => setStack(state => state.filter(item => item != key))

  useEffect(() => {
    if (!isBusy){
      const timeout = setTimeout(() => {
        setIsVisible(false);
      }, successDuration * 1000)

      return () => clearTimeout(timeout);
    }

    setIsVisible(true);
  }, [isBusy])

  useEffect(() => {
    for (const key in loadingIndicators) {
      setStack(state => {
        if (loadingIndicators[key] == true && state.indexOf(key) == -1) {
          state.unshift(key);
        }
        return [...state];
      })
    }
  }, [loadingIndicators])

  return (
    <StatusIndicator className={baseClass} isVisible={isVisible}>
      
      <div className={`${baseClass}__loading-animation-wrapper`}>
        <div className={`${baseClass}__loading-animation-container`}>
          {isBusy ? <StairsAnimation/> : <SuccessAnimation />}
        </div>
      </div>

      <div className={`${baseClass}__loading-feedback-container`}>

        <div className={`${baseClass}__stack-container`}>
          {/* <StackingIndicator
            label={'bigWord'}
            isLoading={true}
            onDismissed={() => {}}
            hasShadow={true} /> */}
          {stack.map((key, index) => (
            <StackingIndicator key={`stacking-indicator-${key}`}
              label={key}
              isLoading={loadingIndicators[key]}
              onDismissed={removeFromStack}
              hasShadow={index === 0}/>
          ))}
        </div>

        <div className={`${baseClass}__message-container`}>
          <p className={`${baseClass}__message`}>
            {isBusy ? (isFetching ? calculatingMessage : loadingMessage) : successMessage}
          </p>
        </div>
      </div>

      

    </StatusIndicator>
  )
}

LoadingStatusIndicator.propTypes = {
  /** Indicate calculating/fetching of new data. */
  isFetching: PropTypes.bool.isRequired
}

LoadingStatusIndicator.defaultProps = {
  /** Used for when dependant calculations are running. */
  calculatingMessage: 'Crunching some numbers...',
  /** Used for when general loading is occuring. */
  loadingMessage: 'Scenario loading...',
  /** Generic success message. */
  successMessage: 'Complete!'
}

export { LoadingStatusIndicator }

const StairsAnimation = () => (
  <div className='stairs-loader'>
    <div className='stairs-loader__bar'/>
    <div className='stairs-loader__bar'/>
    <div className='stairs-loader__bar'/>
    <div className='stairs-loader__bar'/>
    <div className='stairs-loader__bar'/>
    <div className='stairs-loader__ball'/>
  </div>
)

/**
 * Stacking label-like components that are used to show individual loading actions.
 * The input label can allow camelCase labels and will convert to standard title case.
 * There is a forced hang to ensure that the indicator is legible before being dismissed.
 */
const StackingIndicator = ({ isLoading, label, hasShadow, onDismissed }) => {
  const [isVisible, setIsVisible] = useState(false);

  const adjustedLabel = label.charAt(0).toUpperCase() + label.replace(/([A-Z])/g, match => ` ${match.toLowerCase()}`).slice(1);

  useEffect(() => {
    if (!isLoading) {
      const hang = setTimeout(() => {
        setIsVisible(false);
      }, 1200 )

      const dismiss = setTimeout(() => {
        onDismissed(label);
      }, 1500 )

      return () => {
        clearTimeout(hang)
        clearTimeout(dismiss);
      }
    } else {
      setIsVisible(true);
    }
  }, [isLoading])
  return (
    <div className={`${baseClass}__stack ${!isVisible && 'stack-hide-behind'} ${hasShadow && 'shadowed-stack'}`}>

      <p className="mb-0">{adjustedLabel} loading...</p>

    </div>
  )
}

